// useDashboardLayoutPreviewData.js

import { useMemo } from 'react';

import { getResolvedFields } from 'utilities/NodeType';
import { createFilterSortEvaluator } from 'views/utils/filterSortEvaluator';
import { resolveSchemaFieldValue } from 'views/utils/fieldValueResolver';

function normalizeString(value) {
  return String(value || '').trim();
}

function getViewKey(view) {
  return normalizeString(view?.key || view?.id);
}

function getViewTypeKey(view) {
  return normalizeString(view?.nodeType || view?.typeKey);
}

/**
 * deriveDashboardViewNodes
 *
 * Resolves filtered and sorted nodes for a dashboard-linked view.
 *
 * @param {object} options - Resolution options.
 * @returns {array} Resolved node rows.
 */
export function deriveDashboardViewNodes({
  childNodesByParent = {},
  getType,
  userKey = null,
  view,
  baseNodes = [],
}) {
  if (!view) return [];

  const typeKey = getViewTypeKey(view) || 'NODE';
  const typeDef = getType?.(typeKey);

  const sourceNodes = view?.nodeKey
    ? (childNodesByParent[normalizeString(view.nodeKey)] || [])
    : baseNodes;

  const typeFilteredNodes = typeKey
    ? sourceNodes.filter((item) => normalizeString(item?.type) === typeKey)
    : sourceNodes;

  const resolvedProperties = getResolvedFields(typeKey, { includeHidden: true, excludeSecured: true });
  const schemaFieldMap = new Map(
    (Array.isArray(resolvedProperties) ? resolvedProperties : []).map((field) => [field?.name || field?.key, field])
  );

  const resolveFieldValue = (item, fieldDef) => (
    resolveSchemaFieldValue({
      item,
      fieldDef,
      typeKey,
      dataSection: typeDef?.dataSection,
    })
  );

  const { applyFilters, applySorts } = createFilterSortEvaluator({
    resolveFieldValue,
    schemaFieldMap,
    viewerUserKey: userKey,
  });

  const viewFilters = Array.isArray(view?.filters) ? view.filters : [];
  const viewSorts = Array.isArray(view?.sorts) ? view.sorts : [];
  const filtered = viewFilters.length
    ? typeFilteredNodes.filter((item) => applyFilters(item, viewFilters))
    : typeFilteredNodes;

  return viewSorts.length ? applySorts(filtered, viewSorts) : filtered;
}

/**
 * useDashboardLayoutPreviewData
 *
 * Builds layout-editor preview data for dashboard VIEW blocks.
 *
 * @param {object} options - Hook options.
 * @returns {{ nodeByKey: object, rollupPreviewBySelectionKey: Map, viewPreviewByBlockKey: Map }}
 */
export default function useDashboardLayoutPreviewData({
  blocksByKey,
  childNodesByParent = {},
  getType,
  userKey = null,
  views = [],
  baseNodes = [],
}) {
  const viewById = useMemo(() => {
    const map = new Map();

    (Array.isArray(views) ? views : []).forEach((view) => {
      const viewKey = getViewKey(view);
      if (!viewKey) return;
      map.set(viewKey, view);
    });

    return map;
  }, [views]);

  const viewPreviewByBlockKey = useMemo(() => {
    const map = new Map();

    Object.entries(blocksByKey || {}).forEach(([blockKey, block]) => {
      if (normalizeString(block?.type).toUpperCase() !== 'VIEW') {
        return;
      }

      const cleanedBlockKey = normalizeString(blockKey);
      const viewId = normalizeString(block?.viewId);
      const view = viewId ? viewById.get(viewId) || null : null;
      const typeKey = getViewTypeKey(view);

      if (!cleanedBlockKey || !view || !typeKey) {
        return;
      }

      const typeDef = getType?.(typeKey) || null;

      map.set(cleanedBlockKey, {
        nodes: deriveDashboardViewNodes({
          childNodesByParent,
          getType,
          userKey,
          view,
          baseNodes,
        }),
        typeDef,
        view,
      });
    });

    return map;
  }, [blocksByKey, childNodesByParent, getType, userKey, viewById, baseNodes]);

  return {
    nodeByKey: {},
    rollupPreviewBySelectionKey: new Map(),
    viewPreviewByBlockKey,
  };
}
